/**
 * Locale Lazy-Loader — Loads translation JSON files on demand via Vite's
 * import.meta.glob. Only the active language is loaded at runtime.
 *
 * English is imported statically (zero async delay for the default language).
 * All other languages are loaded on first use and cached in memory.
 */

import enTranslations from './en.json';

/**
 * Flatten a potentially nested locale object into a flat Record<string, string>.
 * Supports one level of nesting (section groups) — nested objects have their
 * string values merged into the top-level map. Already-flat objects pass through
 * unchanged, so this is safe to call on any locale JSON.
 */
export function flattenTranslations(
    obj: Record<string, unknown>
): Record<string, string> {
    const flat: Record<string, string> = {};
    for (const [key, value] of Object.entries(obj)) {
        if (typeof value === 'string') {
            flat[key] = value;
        } else if (value && typeof value === 'object' && !Array.isArray(value)) {
            for (const [innerKey, innerValue] of Object.entries(
                value as Record<string, unknown>
            )) {
                if (typeof innerValue === 'string') {
                    flat[innerKey] = innerValue;
                }
            }
        }
    }
    return flat;
}

// Vite glob: each entry is a () => Promise<Record<string, string>>
const localeModules = import.meta.glob<Record<string, string>>(
    './*.json',
    { eager: false, import: 'default' }
) as Record<string, () => Promise<Record<string, string>>>;

/** In-memory cache — once a language is loaded it stays cached for the session */
const cache = new Map<string, Record<string, string>>();
cache.set('en', flattenTranslations(enTranslations as Record<string, unknown>));

/**
 * Load translations for a language code.
 * English is always returned synchronously from cache.
 * Other languages are lazy-loaded on first request, then cached.
 */
export async function loadLocale(langCode: string): Promise<Record<string, string>> {
    const cached = cache.get(langCode);
    if (cached) return cached;

    const key = `./${langCode}.json`;
    const loader = localeModules[key];
    if (!loader) {
        // Unknown language — fall back to English
        return cache.get('en')!;
    }

    const raw = await loader();
    const translations = flattenTranslations(raw as Record<string, unknown>);
    cache.set(langCode, translations);
    return translations;
}

/**
 * Returns the list of available locale codes derived from the JSON filenames.
 * Example: ['en', 'de', 'es-LA', 'fr-CA', 'hi', 'it', 'pt', 'tr', 'zh-CN']
 */
export function getAvailableLocales(): string[] {
    return Object.keys(localeModules).map(
        path => path.replace('./', '').replace('.json', '')
    );
}
